/**
 * Fatture › tutte le fatture emesse da noi, e i preventivi (26/10/2026).
 *
 * La domanda: dov'è quella fattura (o quel preventivo), e com'è finita?
 * La forma: un archivio come l'estratto conto di una banca: per mese, una riga per documento
 * col logo o le iniziali del cliente, lo stato in parole (rosso solo il ritardo) e il totale.
 * In cima solo la ricerca e un "Filtra"; il resto si apre dalla riga.
 *
 * Prima era DocumentsSection (che resta per la pagina vecchia /invoices): due file di pillole
 * per tipo e stato più due menu "Altri…", "Ordina", e una tabella da otto colonne con
 * Tipo di documento, Progetto (quasi sempre vuoto) ed Emesso il. Tolti dalla vista: le pillole
 * (i filtri stanno in "Filtra"), le colonne Progetto e Tipo (il tipo si dice nella riga solo
 * quando non è una fattura), l'ordinamento (per data, il più recente in cima).
 * Si arriva qui da "Tutte le fatture emesse" e "Preventivi" in fondo a Fatture o nel menu ⋯.
 */
import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { InvoiceStatus, QuoteStatus } from '@masamune/shared';
import { Icon } from '../../../components/ui/Icon';
import { BrandLogo } from '../../../components/ui/BrandLogo';
import { EmptyState } from '../../../components/ui/EmptyState';
import { leggibile } from '../../../components/mobile/MobileKit';
import { OrdinaFiltra, useFiltri, type FiltroDef } from '../../../components/ui/OrdinaFiltra';
import { useUrlState } from '../../../hooks/useUrlState';
import { fmtEur } from '../../../lib/utils';
import { documentsQuery, type DocSource, type UnifiedRow } from '../../../lib/scadenziario';
import { daysFromToday } from '../adminApi';
import { aGruppi, meseGruppo } from '../shared/AdminMobile';
import { durata, giornoInParole, ilGiorno, alGiorno, dalGiorno, delGiorno } from './fattureApi';

const PAGINA = 30;

/** Le stesse chiavi dell'indirizzo di prima (`type`, `status`): i link vecchi funzionano ancora. */
const FILTRI_FATTURE: FiltroDef[] = [
  {
    param: 'status', label: 'Stato del pagamento',
    opzioni: [
      { value: InvoiceStatus.SCADUTA, label: 'Solo quelle in ritardo col pagamento' },
      { value: InvoiceStatus.EMESSA, label: 'Solo quelle da incassare' },
      { value: InvoiceStatus.PAGATA, label: 'Solo quelle pagate' },
      { value: InvoiceStatus.BOZZA, label: 'Solo le bozze non emesse' },
      { value: InvoiceStatus.ANNULLATA, label: 'Solo le annullate' },
    ],
  },
  {
    param: 'type', label: 'Tipo di documento',
    opzioni: [
      { value: 'FATTURA', label: 'Solo le fatture' },
      { value: 'NOTA_CREDITO', label: 'Solo le note di credito' },
      { value: 'PROFORMA', label: 'Solo le pre-fatture' },
      { value: 'AVVISO_PAGAMENTO', label: 'Solo gli avvisi di pagamento' },
    ],
  },
];

const FILTRI_PREVENTIVI: FiltroDef[] = [
  {
    param: 'status', label: 'Stato della firma',
    opzioni: [
      { value: QuoteStatus.ACCETTATO, label: 'Solo i firmati' },
      { value: QuoteStatus.INVIATO, label: 'Solo i mandati, non ancora aperti' },
      { value: QuoteStatus.VISUALIZZATO, label: 'Solo gli aperti dal cliente' },
      { value: QuoteStatus.BOZZA, label: 'Solo le bozze' },
      { value: QuoteStatus.CONVERTITO, label: 'Solo quelli diventati fattura' },
      { value: QuoteStatus.RIFIUTATO, label: 'Solo i rifiutati' },
      { value: QuoteStatus.SCADUTO, label: 'Solo gli scaduti senza firma' },
    ],
  },
];

const TIPO_DOC: Record<string, string> = { NOTA_CREDITO: 'Nota di credito', PROFORMA: 'Pre-fattura', AVVISO_PAGAMENTO: 'Avviso di pagamento' };

/** Lo stato in parole, rosso solo il ritardo. */
function stato(r: UnifiedRow): { testo: string; alert?: boolean; mute?: boolean } {
  if (r.kind === 'quote') {
    switch (r.status) {
      case 'BOZZA': return { testo: 'bozza, non mandato', mute: true };
      case 'INVIATO': return { testo: 'mandato, non ancora aperto' };
      case 'VISUALIZZATO': return { testo: 'aperto, non ancora firmato' };
      case 'ACCETTATO': return { testo: 'firmato: da fatturare' };
      case 'CONVERTITO': return { testo: 'diventato fattura', mute: true };
      case 'RIFIUTATO': return { testo: 'rifiutato', mute: true };
      case 'SCADUTO': return { testo: 'scaduto senza firma', mute: true };
      default: return { testo: r.status.toLowerCase() };
    }
  }
  const g = daysFromToday(r.secondaryDate);
  const aperta = ['EMESSA', 'INVIATA', 'SCADUTA'].includes(r.status);
  if (aperta && g != null && g < 0) return { testo: `in ritardo da ${durata(-g)}`, alert: true };
  if (aperta) return { testo: r.secondaryDate ? `da incassare entro ${ilGiorno(r.secondaryDate)}` : 'da incassare' };
  if (r.status === 'PAGATA') return { testo: 'pagata', mute: true };
  if (r.status === 'BOZZA') return { testo: 'bozza, non emessa', mute: true };
  if (r.status === 'ANNULLATA') return { testo: 'annullata', mute: true };
  return { testo: r.status.toLowerCase() };
}

export function FattureArchivio({ tipo, showHistory }: { tipo: 'fatture' | 'preventivi'; showHistory?: boolean }) {
  const navigate = useNavigate();
  const preventivi = tipo === 'preventivi';
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const filtri = useFiltri(preventivi ? FILTRI_PREVENTIVI : FILTRI_FATTURE);
  const [quante, setQuante] = useState(PAGINA);
  const t = filtri.valore('type');
  const source: DocSource = preventivi ? 'quote' : (['FATTURA', 'NOTA_CREDITO', 'PROFORMA', 'AVVISO_PAGAMENTO'].includes(t) ? (t as DocSource) : 'all');
  const q = useQuery({
    ...documentsQuery({ source, status: filtri.valore('status'), search: search.trim(), sort: 'recenti', includeHistory: showHistory, page: 1, pageSize: quante }),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.rows ?? [];
  const total = q.data?.total ?? 0;
  const mesi = useMemo(() => aGruppi(rows, (r) => (r.primaryDate ?? '').slice(0, 7)), [rows]);
  const cercando = !!search.trim() || filtri.attivi > 0;
  const cosa = preventivi ? 'preventivi' : 'documenti';

  return (
    <section className="ft-blocco ft-archivio">
      {/* Senza documenti e senza ricerca, la barra non ha niente da cercare: si vede solo lo stato vuoto. */}
      {(total > 0 || cercando || !q.data) && <div className="ft-archivio-hd">
        <div className="tb-search ft-cerca">
          <Icon name="search" size={12} />
          <input aria-label={preventivi ? 'Cerca fra i preventivi' : 'Cerca fra le fatture emesse'}
            placeholder={preventivi ? 'Cerca per numero o titolo…' : 'Cerca per numero o nota…'}
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <span className="mute text-[12px]">{q.data ? `${total} ${total === 1 ? (preventivi ? 'preventivo' : 'documento') : cosa}` : ''}</span>
        <OrdinaFiltra filtri={filtri} />
      </div>}

      {q.isLoading && !q.data ? <p className="ft-vuoto">Carico {preventivi ? 'i preventivi' : 'le fatture'}…</p>
        : rows.length === 0 ? (
          <EmptyState brand
            title={cercando ? `Nessun ${preventivi ? 'preventivo' : 'documento'} con questa ricerca` : preventivi ? 'Nessun preventivo, per ora' : 'Nessuna fattura emessa'}
            description={cercando ? 'Prova col numero, o togli i filtri.' : preventivi ? 'I preventivi che crei compaiono qui, con lo stato della firma.' : 'Le fatture emesse compaiono qui, anche quelle che arrivano da FattureInCloud.'}
            action={cercando ? <button className="btn" onClick={() => { setSearch(''); filtri.clear(); }}>Togli ricerca e filtri</button> : undefined}
          />
        ) : (
          <>
            {mesi.map((m) => (
              <div key={m.chiave} className="ft-mese">
                <h3 className="ft-mese-hd">{m.chiave ? meseGruppo(m.chiave) : 'Senza data'}</h3>
                <div className="ft-lista">
                  {m.items.map((r) => {
                    const s = stato(r);
                    const tipoDoc = r.invoiceType ? TIPO_DOC[r.invoiceType] : null;
                    return (
                      <button key={`${r.kind}-${r.id}`} type="button" className="ft-doc" onClick={() => navigate(r.kind === 'quote' ? `/quotes/${r.id}` : `/invoices/${r.id}`)}>
                        <BrandLogo name={r.client.legalName} size={32} />
                        <span className="ft-chi">
                          <span className="t">{leggibile(r.client.legalName)}</span>
                          <span className="s">
                            {r.kind === 'quote' ? `${r.subtitle}${r.number ? ` · ${r.number}` : ''}` : `${tipoDoc ?? 'Fattura'} ${r.number ?? 'senza numero'}`}
                          </span>
                        </span>
                        <span className={`ft-doc-stato${s.alert ? ' is-alert' : s.mute ? ' is-mute' : ''}`}>{s.testo}</span>
                        <span className="ft-cifra">€ {fmtEur(Number(r.total), true)}</span>
                        <Icon name="chevronR" size={12} className="mute" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {total > rows.length && (
              <button type="button" className="ft-mostra" disabled={q.isFetching} onClick={() => setQuante((n) => n + PAGINA)}>
                <Icon name="chevronD" size={13} /> {q.isFetching ? 'Carico…' : `Mostra altri ${Math.min(PAGINA, total - rows.length)} ${cosa}`}
              </button>
            )}
          </>
        )}
    </section>
  );
}
